import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "./Motion";

/**
 * Full-viewport cinematic hero (graphicpkg.com style).
 * Uses a Ken-Burns image sequence + parallax + kinetic masked headline overlay.
 * Attempts to autoplay a public MP4 background if available; falls back to the
 * animated image stack (which is the primary "video" experience here).
 */

const HERO_STILLS = [
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/yry5zcyf_AdobeStock_1529584138.webp", alt: "The Puro Pack kraft paper bowls, cups and lids arranged with fresh salads, soups and drinks" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/jfii9597_AdobeStock_579011444.webp", alt: "Two kraft paper bowls filled with rice, chicken, mushrooms, corn and vegetables" },
    { src: "https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/ijc372kd_Untitled-11.webp", alt: "The Puro Pack custom-printed Gelato ice cream paper cup", fit: "contain", position: "top" },
];

export default function VideoHero() {
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const [idx, setIdx] = useState(0);

    // rotate images every ~5.5s
    useEffect(() => {
        if (reduced) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % HERO_STILLS.length), 5500);
        return () => clearInterval(t);
    }, [reduced]);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const scale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [1, 1.15]);
    const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 80]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

    const current = HERO_STILLS[idx];

    return (
        <section ref={ref} className="relative h-[92vh] min-h-[640px] overflow-hidden bg-inkgreen" data-testid="video-hero">
            {/* Cinematic image sequence — Ken Burns crossfade */}
            <motion.div style={{ scale, y, opacity: heroOpacity }} className="absolute inset-0">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={current.src}
                        src={current.src}
                        alt={current.alt}
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 1, scale: reduced ? 1.06 : 1.15 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6.5, ease: "linear" } }}
                        className={`absolute inset-0 w-full h-full ${current.fit === "contain" ? "object-contain bg-bone" : "object-cover"}`}
                        style={current.position ? { objectPosition: `center ${current.position}` } : undefined}
                        draggable={false}
                    />
                </AnimatePresence>
            </motion.div>

            {/* Scrim for legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-inkgreen/75 via-inkgreen/30 to-inkgreen/85 pointer-events-none" />
            <div className="absolute inset-0 bg-inkgreen/15 pointer-events-none" />

            {/* Content overlay */}
            <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-10 flex flex-col justify-between py-14 lg:py-20">
                <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-bone/80">[ Bangalore · Est. 2025 ]</span>
                    <span className="h-px flex-1 bg-bone/30 max-w-[420px]" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-bone/80 hidden md:inline">Green Technology Partner · Sustainable Packaging Mfr.</span>
                </div>

                <div className="max-w-5xl">
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-clay mb-6 lg:mb-8">
                        Sustainable Paper Packaging &middot; Made in Bangalore
                    </div>
                    <MaskedLines
                        className="font-serif font-light text-[14vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.9] tracking-[-0.03em] text-bone"
                        lines={["Your Brand,", <span key="l2"><em className="italic font-normal">Our Eco</em> Promise.</span>]}
                    />
                    <div className="mt-6 lg:mt-10" data-testid="hero-subtitle-container">
                        <p className="sr-only" data-testid="hero-subtitle">
                            A Green Technology partner and sustainable packaging manufacturer, based in Bangalore. Compostable paper salad bowls, soup bowls and PET lids — PE-coated or Aqueous-coated, food safe.
                        </p>
                        <div className="flex flex-wrap gap-2 max-w-3xl">
                            {[
                                "Green Technology Partner",
                                "Sustainable Packaging Mfr.",
                                "Bangalore, India",
                                "Salad · Soup Bowls · PET Lids",
                                "PE · Aqueous coated",
                                "Food-Safe · Compostable",
                            ].map((tag) => (
                                <span key={tag} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest text-bone bg-inkgreen/60 backdrop-blur-sm border border-bone/25">
                                    <span className="w-1 h-1 rounded-full bg-clay"></span>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-end justify-between">
                    <div className="flex items-center gap-3">
                        <ArrowDown className="w-3 h-3 text-bone/70" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-bone/70">Scroll</span>
                        <div className="flex gap-1.5 ml-4">
                            {HERO_STILLS.map((_, i) => (
                                <button key={i} onClick={() => setIdx(i)} aria-label={`Hero slide ${i + 1}`}
                                    data-testid={`hero-dot-${i}`}
                                    className={`h-[2px] transition-all ${idx === i ? "w-10 bg-bone" : "w-5 bg-bone/40 hover:bg-bone/70"}`} />
                            ))}
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-8 text-bone/85">
                        <div><span className="font-serif text-3xl mr-2">2025</span><span className="font-mono text-[11px] uppercase tracking-widest">Founded, BLR</span></div>
                        <div><span className="font-serif text-3xl mr-2">4</span><span className="font-mono text-[11px] uppercase tracking-widest">Product families</span></div>
                        <div><span className="font-serif text-3xl mr-2">100%</span><span className="font-mono text-[11px] uppercase tracking-widest">Food-safe</span></div>
                    </div>
                </div>
            </div>
        </section>
    );
}
